import React, { Component, Suspense } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import debounce from 'lodash/debounce';
import uniqueId from 'lodash/uniqueId';
import set from 'lodash/set';
import cloneDeep from 'lodash/cloneDeep';
import Toggle from 'react-toggle';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-toggle/style.css';
import { initialOptions } from '../../options/defaults';
import {
    INSPECTOR_TABS,
    getInspectorTabIndexFromSlug,
    getInspectorTabPath,
    getInspectorTabSlugFromPath,
    syncInspectorTabUrl,
} from './tabRoutes';
import { syncInspectorTabSeo } from './tabSeo';
import { LAZY_TAB_COMPONENTS } from './lazyTabs';
import { createImageLayer, createTextLayer } from '../../engine/behindSubject';
import { POSITION_LABELS } from '../../engine/watermark/positions';
import { trackEvent, trackTabViewed } from '../../analytics';

export default class Inspector extends Component {
    static defaultProps = {
        hasImages: false,
        hasSelection: false,
        selected: [],
        isProcessing: false,
        onUpdate: () => Promise.resolve(),
        onDownload: () => Promise.resolve(),
        onTabChange: () => {},
        focusedImageId: null,
        onBackgroundLayersChange: () => {},
        onFirstBehindLayerAdded: () => Promise.resolve(),
        allImageIds: [],
        inspectorApi: null,
    };

    static propTypes = {
        hasImages: PropTypes.bool,
        hasSelection: PropTypes.bool,
        selected: PropTypes.arrayOf(PropTypes.string),
        isProcessing: PropTypes.bool,
        onUpdate: PropTypes.func,
        onDownload: PropTypes.func,
        onTabChange: PropTypes.func,
        focusedImageId: PropTypes.string,
        onBackgroundLayersChange: PropTypes.func,
        onFirstBehindLayerAdded: PropTypes.func,
        allImageIds: PropTypes.arrayOf(PropTypes.string),
        inspectorApi: PropTypes.object,
    };

    constructor(...args) {
        super(...args);
        const initialSlug = getInspectorTabSlugFromPath(window.location.pathname);
        this.state = {
            changing: false,
            options: cloneDeep(initialOptions),
            tabIndex: getInspectorTabIndexFromSlug(initialSlug),
            hasApplied: false,
            deferredTabsReady: false,
        };
        this.onUpdate = debounce(this.onUpdate, 1000);
        this.overlayInputRef = React.createRef();
    }

    componentDidMount() {
        const slug = getInspectorTabSlugFromPath(window.location.pathname);
        const path = getInspectorTabPath(slug);

        if (window.location.pathname !== path) {
            syncInspectorTabUrl(slug, { replace: true });
        }

        this.notifyTabChange(slug, 'landing');
        window.addEventListener('popstate', this.handlePopState);
        this.registerInspectorApi();
        this.syncBackgroundLayersToApp();
        this.scheduleDeferredTabs();
    }

    scheduleDeferredTabs = () => {
        const enable = () => {
            if (this.state.deferredTabsReady) {
                return;
            }
            this.setState({ deferredTabsReady: true });
        };

        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(enable, { timeout: 2000 });
        } else {
            window.setTimeout(enable, 1);
        }
    };

    componentWillUnmount() {
        window.removeEventListener('popstate', this.handlePopState);
    }

    componentDidUpdate(prevProps, prevState) {
        const prevSelected = prevProps.selected || [];
        const nextSelected = this.props.selected || [];
        const addedSelection = nextSelected.some(id => !prevSelected.includes(id));

        if (addedSelection && nextSelected.length > 0 && this.props.hasImages) {
            this.props.onUpdate(this.state.options);
        }

        if (prevProps.hasImages !== this.props.hasImages) {
            this.setState({ hasApplied: false });
        }

        if (prevProps.focusedImageId !== this.props.focusedImageId
            || prevState.options.background !== this.state.options.background) {
            this.syncBackgroundLayersToApp();
        }
    }

    render() {
        const { options } = this.state;
        const tabProps = {
            options,
            updateOption: this.updateOption,
            onMouseDown: this.onMouseDown,
            onMouseUp: this.onMouseUp,
        };

        return (
            <div className="flex h-full min-h-0 flex-col">
                <Tabs
                    className="picinez-tabs flex-1 min-h-0"
                    selectedIndex={this.state.tabIndex}
                    onSelect={this.handleTabSelect}
                >
                    <TabList>
                        { INSPECTOR_TABS.map(tab => (
                            <Tab key={tab.slug}>{ tab.label }</Tab>
                        )) }
                    </TabList>
                    { INSPECTOR_TABS.map((tab, index) => (
                        <TabPanel key={tab.slug} className="custom-scrollbar">
                            { this.state.tabIndex === index && (
                                this.state.deferredTabsReady
                                    ? this.renderLazyTab(tab.slug, tabProps)
                                    : <div className="p-4 text-body-md text-secondary">Loading…</div>
                            ) }
                        </TabPanel>
                    )) }
                </Tabs>
                { this.renderButtons() }
            </div>
        );
    }

    renderLazyTab = (slug, tabProps) => {
        const panelClassName = slug === 'settings' ? 'space-y-4' : 'space-y-1';

        return (
            <Suspense fallback={<div className="p-4 text-body-md text-secondary">Loading tab…</div>}>
                <div className={panelClassName}>{ this.renderTabContent(slug, tabProps) }</div>
            </Suspense>
        );
    };

    renderTabContent = (slug, tabProps) => {
        const TabComponent = LAZY_TAB_COMPONENTS[slug];

        switch (slug) {
        case 'background':
            return (
                <TabComponent
                    {...tabProps}
                    focusedImageId={this.props.focusedImageId}
                    disabled={this.props.isProcessing}
                    onAddTextLayer={this.addTextLayer}
                    onAddImageLayer={this.addImageLayer}
                    onUpdateLayer={this.updateLayer}
                    onRemoveLayer={this.removeLayer}
                    onMoveLayer={this.moveLayer}
                    onDuplicateLayer={this.duplicateLayer}
                    onTogglePerImageCustomize={this.togglePerImageCustomize}
                    onResetPerImageLayers={this.resetPerImageLayers}
                    onLayerPositionCommit={this.apply}
                />
            );
        case 'watermarks':
            return (
                <TabComponent
                    {...tabProps}
                    overlayInputRef={this.overlayInputRef}
                    onOverlayFileInput={this.onOverlayFileInput}
                    onOverlayFiles={this.onOverlayFiles}
                    onWatermarkPositionDrop={this.onWatermarkPositionDrop}
                    onWatermarkPositionClick={this.onWatermarkPositionClick}
                    renderPositionCheckbox={this.renderPositionCheckbox}
                    openFiles={this.openFiles}
                    updateWatermarkColor={this.updateWatermarkColor}
                />
            );
        default:
            return <TabComponent {...tabProps} />;
        }
    };

    handleTabSelect = (index) => {
        const slug = INSPECTOR_TABS[index].slug;
        this.setState({ tabIndex: index });
        syncInspectorTabUrl(slug);
        this.notifyTabChange(slug, 'click');
    };

    handlePopState = () => {
        const slug = getInspectorTabSlugFromPath(window.location.pathname);
        this.setState({ tabIndex: getInspectorTabIndexFromSlug(slug) });
        this.notifyTabChange(slug, 'navigation');
    };

    notifyTabChange = (slug, entry = 'click') => {
        trackTabViewed(slug, entry);
        syncInspectorTabSeo(slug);
        this.props.onTabChange(slug);
    };

    registerInspectorApi = () => {
        if (!this.props.inspectorApi) {
            return;
        }

        this.props.inspectorApi.setLayerPosition = this.setLayerPosition;
    };

    syncBackgroundLayersToApp = () => {
        if (!this.props.onBackgroundLayersChange) {
            return;
        }

        const { options } = this.state;
        if (!options.background.behindSubject) {
            this.props.onBackgroundLayersChange(null);
            return;
        }

        this.props.onBackgroundLayersChange({
            behindSubject: true,
            focusedImageId: this.props.focusedImageId,
            layers: this.getEditableLayers(),
        });
    };

    getEditableLayers = () => {
        const { options } = this.state;
        const { focusedImageId } = this.props;

        if (focusedImageId && options.background.perImage[focusedImageId]?.useCustomLayers) {
            return options.background.perImage[focusedImageId].layers || [];
        }

        return options.background.layers || [];
    };

    isUsingPerImageLayers = () => {
        const { focusedImageId } = this.props;
        return !!(focusedImageId && this.state.options.background.perImage[focusedImageId]?.useCustomLayers);
    };

    getSegmentationImageIds = () => {
        const { focusedImageId, allImageIds } = this.props;

        if (this.isUsingPerImageLayers() && focusedImageId) {
            return [focusedImageId];
        }

        return allImageIds;
    };

    setLayers = (layers, forceUpdate = true, onComplete) => {
        const { focusedImageId } = this.props;
        const newOptions = { ...this.state.options };

        if (this.isUsingPerImageLayers()) {
            set(newOptions, `background.perImage.${focusedImageId}.layers`, layers);
        } else {
            set(newOptions, 'background.layers', layers);
        }

        this.setState({ options: newOptions }, () => {
            if (this.props.onBackgroundLayersChange && newOptions.background.behindSubject) {
                this.props.onBackgroundLayersChange({
                    behindSubject: true,
                    focusedImageId: this.props.focusedImageId,
                    layers,
                });
            }

            if (forceUpdate) {
                this.onUpdate();
            }

            if (onComplete) {
                onComplete();
            }
        });
    };

    handleFirstLayerAdded = () => {
        this.setState({ hasApplied: true }, () => {
            this.props.onFirstBehindLayerAdded(this.state.options, this.getSegmentationImageIds());
        });
    };

    addTextLayer = () => {
        const prevLayers = this.getEditableLayers();
        const isFirstLayer = prevLayers.length === 0;
        const newLayers = [...prevLayers, createTextLayer()];

        if (isFirstLayer && this.state.options.background.behindSubject) {
            this.setLayers(newLayers, false, this.handleFirstLayerAdded);
            trackEvent('behind_layer_added', { layer_type: 'text' });
            return;
        }

        this.setLayers(newLayers);
        trackEvent('behind_layer_added', { layer_type: 'text' });
    };

    addImageLayer = (imageSrc) => {
        const prevLayers = this.getEditableLayers();
        const isFirstLayer = prevLayers.length === 0;
        const newLayers = [...prevLayers, createImageLayer(imageSrc)];

        if (isFirstLayer && this.state.options.background.behindSubject) {
            this.setLayers(newLayers, false, this.handleFirstLayerAdded);
            trackEvent('behind_layer_added', { layer_type: 'image' });
            return;
        }

        this.setLayers(newLayers);
        trackEvent('behind_layer_added', { layer_type: 'image' });
    };

    updateLayer = (layerId, patch, forceUpdate = true) => {
        const layers = this.getEditableLayers().map(layer => (
            layer.id === layerId ? { ...layer, ...patch } : layer
        ));
        this.setLayers(layers, forceUpdate);
    };

    removeLayer = (layerId) => {
        this.setLayers(this.getEditableLayers().filter(layer => layer.id !== layerId));
    };

    moveLayer = (layerId, direction) => {
        const layers = [...this.getEditableLayers()];
        const index = layers.findIndex(layer => layer.id === layerId);

        if (index < 0) {
            return;
        }

        const target = index + direction;
        if (target < 0 || target >= layers.length) {
            return;
        }

        const [item] = layers.splice(index, 1);
        layers.splice(target, 0, item);
        this.setLayers(layers);
    };

    duplicateLayer = (layerId) => {
        const layers = [...this.getEditableLayers()];
        const index = layers.findIndex(layer => layer.id === layerId);

        if (index < 0) {
            return;
        }

        const copy = {
            ...cloneDeep(layers[index]),
            id: uniqueId('layer_'),
            name: `${layers[index].name || 'Layer'} copy`,
        };
        layers.splice(index + 1, 0, copy);
        this.setLayers(layers);
    };

    setLayerPosition = (layerId, x, y) => {
        const layers = this.getEditableLayers().map(layer => (
            layer.id === layerId ? { ...layer, x, y } : layer
        ));
        this.setLayers(layers, false, () => {
            this.apply();
        });
    };

    togglePerImageCustomize = (imageId, enabled) => {
        const { options } = this.state;
        const perImage = { ...options.background.perImage };

        if (enabled) {
            perImage[imageId] = {
                useCustomLayers: true,
                layers: cloneDeep(options.background.layers),
            };
        } else {
            delete perImage[imageId];
        }

        this.updateOption('background.perImage', perImage);
        this.syncBackgroundLayersToApp();
    };

    resetPerImageLayers = (imageId) => {
        const perImage = { ...this.state.options.background.perImage };
        delete perImage[imageId];
        this.updateOption('background.perImage', perImage);
        this.syncBackgroundLayersToApp();
    };

    renderPositionCheckbox = (options, position) => {
        const checked = options.watermark.position[position];
        const angle = options.watermark.angle;
        return (
            <input
                key={position + angle + checked}
                aria-label={POSITION_LABELS[position] || position}
                className={cx('wm-position-cell', {
                    dragover: options.watermark.dragOver[position],
                })}
                onDragEnter={() => this.updateOption(`watermark.dragOver.${position}`, true)}
                onDragLeave={() => this.updateOption(`watermark.dragOver.${position}`, false)}
                onDragOver={e => { e.stopPropagation(); e.preventDefault(); }}
                onDrop={() => this.onWatermarkPositionDrop(position, options)}
                type="checkbox"
                style={{
                    transform: `rotate(-${angle}deg)`,
                    backgroundImage: options.watermark.imagesPosition[position]
                        ? `url('${options.watermark.imagesPosition[position]}')`
                        : undefined,
                    backgroundSize: 'cover',
                }}
                checked={checked}
                onChange={e => this.onWatermarkPositionClick(position, e.target.checked)}
            />
        );
    };

    renderButtons() {
        const { hasImages, hasSelection, isProcessing } = this.props;
        const { autoApply } = this.state.options;
        const runDisabled = !hasImages || isProcessing;
        const downloadSelectedDisabled = runDisabled || !hasSelection;
        return (
            <div className="shrink-0 border-t border-border-subtle bg-white p-4 desk:static fixed bottom-0 left-0 right-0 z-30 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
                <div className="mx-auto flex max-w-inspector flex-col gap-3">
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            className="rounded-lg bg-primary py-3 text-body-md font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={runDisabled}
                            onClick={this.downloadAll}
                        >
                            Download All
                        </button>
                        <button
                            type="button"
                            className="rounded-lg bg-primary py-3 text-body-md font-semibold text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={downloadSelectedDisabled}
                            onClick={this.downloadSelected}
                        >
                            Download Selected
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <button
                            type="button"
                            className="rounded-lg border border-border-subtle py-2.5 text-body-md font-medium text-on-surface transition-colors hover:bg-surface disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={isProcessing}
                            onClick={this.reset}
                        >
                            Reset
                        </button>
                        <button
                            type="button"
                            className="rounded-lg border border-primary py-2.5 text-body-md font-medium text-primary transition-colors hover:bg-primary/5 disabled:cursor-not-allowed disabled:opacity-50"
                            disabled={runDisabled}
                            onClick={this.apply}
                        >
                            Apply
                        </button>
                    </div>
                    <div className="flex items-center justify-between gap-3 border-t border-border-subtle pt-3">
                        <label htmlFor="option-auto" className="cursor-pointer text-body-md text-on-surface">Autorun on Change</label>
                        <Toggle
                            key={autoApply}
                            id="option-auto"
                            defaultChecked={autoApply}
                            onChange={(e) => this.handleAutoApplyChange(e.target.checked)}
                        />
                    </div>
                </div>
            </div>
        );
    }

    openFiles = (files = []) => {
        const { options } = this.state;
        if (files.length > 0) {
            this.setState({
                options: {
                    ...options,
                    watermark: {
                        ...options.watermark,
                        images: [...options.watermark.images, ...files.map(file => ({
                            id: uniqueId(),
                            src: URL.createObjectURL(file),
                        }))],
                    },
                },
            });
        }
    };

    onOverlayFiles = (files = []) => {
        const { options } = this.state;
        if (files.length > 0) {
            const src = URL.createObjectURL(files[0]);
            this.setState({
                options: {
                    ...options,
                    watermark: {
                        ...options.watermark,
                        imagesPosition: {
                            ...options.watermark.imagesPosition,
                            overlay: src,
                        },
                        images: [
                            ...options.watermark.images,
                            { id: uniqueId(), src },
                        ],
                    },
                },
            });
        }
    };

    onOverlayFileInput = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            this.onOverlayFiles(Array.from(e.target.files));
            e.target.value = '';
        }
    };

    reset = () => {
        this.setState({
            options: {
                ...cloneDeep(initialOptions),
                autoApply: this.state.options.autoApply,
            },
            hasApplied: false,
        }, () => {
            if (this.state.options.autoApply) {
                this.apply();
            }
            this.forceUpdate();
        });
    };

    apply = () => {
        this.onUpdate.cancel();
        this.setState({ hasApplied: true }, () => {
            this.props.onUpdate(this.state.options);
        });
    };

    handleAutoApplyChange = (checked) => {
        this.setState(
            prevState => ({
                options: { ...prevState.options, autoApply: checked },
            }),
            () => {
                if (checked && !this.state.hasApplied && this.props.hasImages && !this.props.isProcessing) {
                    this.apply();
                }
            }
        );
    };

    downloadAll = () => {
        this.props.onDownload(this.state.options, 'all');
    };

    downloadSelected = () => {
        this.props.onDownload(this.state.options, 'selected');
    };

    onMouseDown = () => {
        this.setState({ changing: true });
    };

    onMouseUp = () => {
        this.setState({ changing: false });
    };

    onUpdate(onApply = false) {
        if ((!this.state.changing && this.state.options.autoApply) || onApply) {
            this.setState({ hasApplied: true });
            this.props.onUpdate(this.state.options);
        }
    }

    updateWatermarkColor = (newColor) => {
        const color = `rgba(${newColor.rgb.r}, ${newColor.rgb.g}, ${newColor.rgb.b}, ${newColor.rgb.a})`;
        this.updateOption('watermark.color', color);
    };

    updateOption = (name, value, forceUpdate = true) => {
        const newOptions = { ...this.state.options };
        set(newOptions, name, value);
        this.setState({ options: newOptions }, () => {
            if (forceUpdate) {
                this.onUpdate();
            }
        });
    };

    onWatermarkPositionDrop = (position, options) => {
        this.updateOption(`watermark.imagesPosition.${position}`, options.watermark.draggedImage);
        this.updateOption(`watermark.dragOver.${position}`, false);
        if (position !== 'overlay') {
            this.updateOption(`watermark.position.${position}`, false);
        }
    };

    onWatermarkPositionClick = (position, checked) => {
        const { options } = this.state;
        if (options.watermark.imagesPosition[position]) {
            this.updateOption(`watermark.imagesPosition.${position}`, false);
            if (position !== 'overlay') {
                this.updateOption(`watermark.position.${position}`, false);
            }
        } else if (position !== 'overlay') {
            this.updateOption(`watermark.position.${position}`, checked);
        }
    };
}
